import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import PaymentData = GooglePay.PaymentData;
import { PaymentProviderComponentInterface } from '../payment-provider.component.interface';
import { GooglePay } from '../gpay/client/client';
import { OrderDataProvider } from 'libs/core/src/lib/data-provider/order.data-provider';
import { PaymentConfigViewModel } from 'libs/core/src/lib/model/view-model/payment/config/payment.config.view.model';
import { OrderStateService } from 'libs/core/src/lib/state/order.state.service';
import { CreatePaymentRedirectUrl } from '../../../create-payment-redirect-url';
import { PaymentHelperService } from '../../../service/payment-helper.service';
import { PaymentProviderStateService } from '../../../service/payment-provider-state.service';
import {
  PaymentProviderEvent,
  DonePaymentProviderEvent,
  ErrorPaymentProviderEvent,
  WorkPaymentProviderEvent,
  ErrorPaymentKeyProviderEvent
} from '../../event/payment-provider.event';
import { PaymentMethodEnum } from './payment-method.enum';
import { OrderPaymentPackage } from '../../../../../../../../core/src/lib/model/order-payment.package.model';
import { PaymentTotalPriceStatus, PaymentAuthMethod, PaymentCardNetwork, PaymentDataModel } from '../gpay/model/payment-data.model';
import { GooglePayService, CreatePaymentDataRequestDto } from '../gpay/service/google-pay.service';
import { PayuExpressService } from './service/payu-express.service';
import { ENVIRONMENT_TOKEN, ProviderEnum, sanitizeInput } from 'libs/core/src/public-api';
import { PaymentProviderPayMethodViewModel } from 'libs/core/src/lib/model/view-model/payment-provider-pay-method.view.model';
import { PaymentViewModel } from 'libs/core/src/lib/model/view-model/payment.view.model';
import { FinancialHelper } from '@lib/core';
import { OrderViewModel } from 'libs/core/src/lib/model/view-model/order/order.view.model';
import { generalPaymentError } from '../../../payment-error.consts';
import { ApplePayService } from '../apay/service/apple-pay.service';
import { OrderPaymentRequestModel } from 'libs/core/src/lib/model/request/order-payment.request.model';

declare let ApplePaySession: any | undefined;

const internalPaymentMethodType = {
  googlePayToken: 'GOOGLEPAY_TOKEN',
  cardToken: 'CARD_TOKEN',
  blikToken: 'BLIK',
  blikCode: 'BLIK_T6',
  payByLink: 'PBL',
  applePayToken: 'APPLEPAY_TOKEN'
};

@Component({
  selector: 'app-payment-provider-payu-component',
  templateUrl: './payu-payment-provider.component.html',
  standalone: false
})
export class PayuPaymentProviderComponent implements PaymentProviderComponentInterface, OnInit, OnDestroy {
  @Output()
  public events: EventEmitter<PaymentProviderEvent> = new EventEmitter<PaymentProviderEvent>();

  public blikWaitSecondsForTransactionStep = 7;

  public cardWaitSecondsForTransactionStep = 4;

  public paymentMethod = PaymentMethodEnum;

  public readonly paymentProviderIdentifier: string = ProviderEnum.PAYU;

  /**
   * The collection of payByLinks methods
   */
  public payByLinkMethodCollection: Array<PaymentProviderPayMethodViewModel> = new Array<PaymentProviderPayMethodViewModel>();

  /**
   * The collection of card methods
   */
  public cardMethodCollection: Array<PaymentProviderPayMethodViewModel> = new Array<PaymentProviderPayMethodViewModel>();

  /**
   * Selected pay by link method by user
   */
  public selectedPayByLinkMethod: PaymentProviderPayMethodViewModel | null = null;

  /**
   * Selected card method
   */
  public selectedCardMethod: PaymentProviderPayMethodViewModel | null = null;

  /**
   * Selected payment provider pay method
   */
  public selectedPayMethodTypeCustom: string | null = null;

  public paymentProviderPayMethodCollection: Array<PaymentProviderPayMethodViewModel> = new Array<PaymentProviderPayMethodViewModel>();

  public paymentProviderConfig: PaymentConfigViewModel | null;

  public form: FormGroup;
  public payuCardErrorMessages: Array<string> = [];
  public readonly secureFormContainerId: string = `payu-card-secure-form-${Math.random().toString(36).slice(2)}`;
  public readonly secureFormNumberContainerId: string = `${this.secureFormContainerId}-number`;
  public readonly secureFormDateContainerId: string = `${this.secureFormContainerId}-date`;
  public readonly secureFormCvvContainerId: string = `${this.secureFormContainerId}-cvv`;

  private paymentProviderInitSubscription: Subscription = Subscription.EMPTY;

  private order: OrderViewModel = null;
  private blikNumber: string = null;

  private readonly paymentChannel: string = 'web';
  public formSubmitAttempt = false;
  public formSubmitAttempt2 = false;

  private continueUrl: string;
  private isApplePaySessionDefined = false;
  private isPayuSecureFormInitialized = false;
  private isPayuSecureFormInitInProgress = false;
  private secureFormInitTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private payuSdkLoadPromise: Promise<void> | null = null;

  public constructor(
    @Inject(ENVIRONMENT_TOKEN) protected environment: any,
    private orderDataProvider: OrderDataProvider,
    private orderStateService: OrderStateService,
    private paymentHelperService: PaymentHelperService,
    private route: ActivatedRoute,
    private payuExpressService: PayuExpressService,
    private googlePayService: GooglePayService,
    private applePayService: ApplePayService,
    private paymentProviderStateService: PaymentProviderStateService
  ) {
    this.paymentChannel = this.environment?.constants?.paymentChannel ?? 'web';
    this.initialComponent();

    this.paymentProviderStateService.state$.subscribe((state) => {
      if (state) {
        this.selectedPayMethodTypeCustom = state.type;
        this.selectedPayByLinkMethod = state.payByLink;

        if (state.type === PaymentMethodEnum.TYPE_CARD) {
          this.scheduleSecureFormInit();
        } else {
          this.payuExpressService.destroySecureCardForm();
          this.isPayuSecureFormInitialized = false;

          if (this.secureFormInitTimeoutId !== null) {
            clearTimeout(this.secureFormInitTimeoutId);
            this.secureFormInitTimeoutId = null;
          }
        }
      } else {
        this.selectedPayMethodTypeCustom = null;
        this.selectedPayByLinkMethod = null;

        this.payuExpressService.destroySecureCardForm();
        this.isPayuSecureFormInitialized = false;
      }
    });
  }

  public ngOnInit(): void {}

  private initialComponent() {
    this.events.next(new WorkPaymentProviderEvent());
    this.order = this.orderStateService.getOrder();
    this.form = this.paymentProviderStateService.getBlikFormGroup();
    this.initPaymentProviderWidget();

    if (this.applePayService.checkAvailability()) {
      this.isApplePaySessionDefined = true;
    }
  }

  public ngOnDestroy(): void {
    if (this.paymentProviderInitSubscription !== Subscription.EMPTY) {
      this.paymentProviderInitSubscription.unsubscribe();
      this.paymentProviderInitSubscription = Subscription.EMPTY;
    }

    this.form?.reset();
    this.payuExpressService.destroySecureCardForm();
    this.isPayuSecureFormInitialized = false;

    if (this.secureFormInitTimeoutId !== null) {
      clearTimeout(this.secureFormInitTimeoutId);
      this.secureFormInitTimeoutId = null;
    }
  }

  public onPostInitPayment(paymentModel: PaymentViewModel): void {
    switch (this.selectedPayMethodTypeCustom) {
      case PaymentMethodEnum.TYPE_PBL:
      case PaymentMethodEnum.TYPE_GPAY:
      case PaymentMethodEnum.TYPE_APAY:
      case PaymentMethodEnum.TYPE_CARD:
        window.location.href = paymentModel.plainPayload;
        break;

      case PaymentMethodEnum.TYPE_BLIK:
        const request: CreatePaymentRedirectUrl = new CreatePaymentRedirectUrl();
        request.paymentChannel = this.paymentChannel;
        request.order = this.order;
        request.route = this.route;
        window.location.href = this.paymentHelperService.createPaymentRedirectUrl(request, this.continueUrl);
        break;

      default:
        break;
    }
  }

  public onPreInitPayment(event: OrderPaymentPackage): Observable<OrderPaymentPackage> {
    this.continueUrl = event.continueUrl;
    return new Observable<OrderPaymentPackage>((subscriber) => {
      switch (this.selectedPayMethodTypeCustom) {
        case null:
          event.abort = true;
          this.emitNotSelectedPaymentProvider();
          subscriber.next(event);
          subscriber.complete();
          break;

        case PaymentMethodEnum.TYPE_PBL:
          if (!this.selectedPayByLinkMethod) {
            event.abort = true;
            this.emitNotSelectedPaymentProvider();
          } else {
            event.intPayMethodType = internalPaymentMethodType.payByLink;
            event.intPayMethodValue = this.selectedPayByLinkMethod.id;
          }
          subscriber.next(event);
          subscriber.complete();
          break;

        case PaymentMethodEnum.TYPE_BLIK:
          this.formSubmitAttempt = true;
          if (this.form.valid) {
            const blikTokenPayMethod: PaymentProviderPayMethodViewModel | null = this.getBLIKTokenPayMethod();
            const filteredInputBlikCode: string | null = sanitizeInput(this.form.get('blikCode').value);
            if (filteredInputBlikCode === null && blikTokenPayMethod !== null) {
              event.intPayMethodType = internalPaymentMethodType.blikToken;
              event.intPayMethodValue = blikTokenPayMethod.id;
            } else if (filteredInputBlikCode !== null) {
              event.intPayMethodType = internalPaymentMethodType.blikCode;
              event.intPayMethodValue = filteredInputBlikCode;
            } else {
              event.abort = true;
            }
          } else {
            event.abort = true;
          }

          subscriber.next(event);
          subscriber.complete();
          break;

        case PaymentMethodEnum.TYPE_CARD:
          this.formSubmitAttempt2 = true;
          this.payuCardErrorMessages = [];

          if (this.selectedCardMethod !== null) {
            event.intPayMethodType = internalPaymentMethodType.cardToken;
            event.intPayMethodValue = this.selectedCardMethod.id;
            subscriber.next(event);
            subscriber.complete();
          } else {
            this.payuExpressService.getCardToken(this.paymentProviderConfig.salesPointId, this.secureFormSelectors).subscribe({
              next: (body) => {
                event.intPayMethodType = internalPaymentMethodType.cardToken;
                event.intPayMethodValue = body.token;

                subscriber.next(event);
                subscriber.complete();
              },
              error: (e: HttpErrorResponse) => {
                console.error('error', e);
                this.payuCardErrorMessages = this.payuExpressService.extractErrorMessages(e);
                this.events.next(new ErrorPaymentProviderEvent(e));

                event.abort = true;
                subscriber.next(event);
                subscriber.complete();
              }
            });
          }
          break;

        case PaymentMethodEnum.TYPE_GPAY:
          const googlePayClient: GooglePay.PaymentsClient = this.googlePayService.getClient(this.paymentProviderConfig.environment);
          const orderPrice: number = this.order.valueToPay;
          const createPaymentRequestDataDto: CreatePaymentDataRequestDto = new CreatePaymentDataRequestDto();
          createPaymentRequestDataDto.merchantId = this.paymentProviderConfig.merchantId;
          createPaymentRequestDataDto.merchantName = this.paymentProviderConfig.merchantName;
          createPaymentRequestDataDto.gateway = 'payu';
          createPaymentRequestDataDto.gatewayMerchantId = this.paymentProviderConfig.salesPointId;
          createPaymentRequestDataDto.totalPriceStatus = PaymentTotalPriceStatus.FINAL;
          createPaymentRequestDataDto.totalPrice = FinancialHelper.fixedValue(orderPrice);
          createPaymentRequestDataDto.currencyCode = this.environment.constants.currency;
          createPaymentRequestDataDto.countryCode = this.environment.constants.country;
          createPaymentRequestDataDto.allowedCardAuthMethods = [PaymentAuthMethod.PAN_ONLY, PaymentAuthMethod.CRYPTOGRAM_3DS];
          createPaymentRequestDataDto.allowedCardMethods = [PaymentCardNetwork.VISA, PaymentCardNetwork.MASTERCARD];
          const paymentDataRequest: PaymentDataModel = this.googlePayService.createPaymentDataRequest(createPaymentRequestDataDto);
          googlePayClient
            .loadPaymentData(paymentDataRequest as any)
            .then((paymentData: PaymentData) => {
              const token: string | null =
                paymentData &&
                paymentData.paymentMethodData &&
                paymentData.paymentMethodData.tokenizationData &&
                paymentData.paymentMethodData.tokenizationData.token
                  ? paymentData.paymentMethodData.tokenizationData.token
                  : null;
              event.intPayMethodType = internalPaymentMethodType.googlePayToken;
              event.intPayMethodValue = btoa(token as string);
              subscriber.next(event);
              subscriber.complete();
            })
            .catch((err) => {
              event.abort = true;
              this.events.next(new ErrorPaymentProviderEvent(err));
              subscriber.next(event);
              subscriber.complete();
            });
          break;

        case PaymentMethodEnum.TYPE_APAY:
          this.applePayService.init(this.paymentProviderConfig.merchantName);
          const session = this.applePayService.createApplePaySession(this.order.valueToPay);

          session.onvalidatemerchant = () => {
            this.orderDataProvider.getApplePaySession(this.order.cinemaId, this.order.id, this.paymentProviderIdentifier).subscribe({
              next: (merchantSession) => {
                session.completeMerchantValidation(merchantSession);
              },
              error: (error: any) => {
                event.abort = true;
                this.events.next(new ErrorPaymentProviderEvent(error));
                subscriber.next(event);
                subscriber.complete();

                session.abort();
              }
            });
          };

          session.onpaymentauthorized = (paymentEvent) => {
            session.completePayment({
              status: ApplePaySession.STATUS_SUCCESS
            });

            const token: string | null =
              paymentEvent && paymentEvent.payment && paymentEvent.payment.token && paymentEvent.payment.token.paymentData
                ? JSON.stringify(paymentEvent.payment.token.paymentData)
                : null;
            event.intPayMethodType = internalPaymentMethodType.applePayToken;
            event.intPayMethodValue = btoa(token as string);
            subscriber.next(event);
            subscriber.complete();
          };

          session.begin();
          break;

        default:
          event.abort = true;
          this.emitNotSelectedPaymentProvider();
          subscriber.next(event);
          subscriber.complete();
          break;
      }
    });
  }

  private getPayMethod(id: string) {
    return this.paymentProviderPayMethodCollection.find((x) => x.id && x.id.toLocaleLowerCase() === id);
  }

  private getTokenPayMethod(type: string) {
    return this.paymentProviderPayMethodCollection.find((x) => x.type && x.type.toLocaleLowerCase() === type);
  }

  public getBLIKPayMethod(): PaymentProviderPayMethodViewModel | null {
    return this.getPayMethod('blik');
  }

  public getBLIKTokenPayMethod(): PaymentProviderPayMethodViewModel | null {
    return this.getTokenPayMethod('blik');
  }

  public getCardPayMethod(): PaymentProviderPayMethodViewModel | null {
    return this.getPayMethod('c');
  }

  public getGooglePayPayMethod(): PaymentProviderPayMethodViewModel | null {
    return this.getPayMethod('ap');
  }

  public getApplePayPayMethod(): PaymentProviderPayMethodViewModel | null {
    if (navigator.appVersion.indexOf('Mac') !== -1) {
      return this.isApplePaySessionDefined && this.getPayMethod('jp');
    }
  }

  public getPayByLinkPayMethodCollection(): Array<PaymentProviderPayMethodViewModel> {
    return this.paymentProviderPayMethodCollection.filter((x) => x.type === 'PBL' && x.id !== 'ap' && x.id !== 'c' && x.id !== 'blik' && x.id != 'jp');
  }

  public getCardMethodCollection(): Array<PaymentProviderPayMethodViewModel> {
    return this.paymentProviderPayMethodCollection.filter((x) => x.type && x.type.toLocaleLowerCase() === 'card_token' && x.isPreferred);
  }

  public onClickedPayByLinkMethod(paymentProviderPayMethod: PaymentProviderPayMethodViewModel): void {
    this.selectedPayByLinkMethod = paymentProviderPayMethod;
    const state = this.paymentProviderStateService.getState();
    this.paymentProviderStateService.setState({ ...state, payByLink: paymentProviderPayMethod });
  }

  public onClickedCardMethod(paymentProviderPayMethod: PaymentProviderPayMethodViewModel): void {
    if (this.selectedCardMethod !== paymentProviderPayMethod) {
      this.selectedCardMethod = paymentProviderPayMethod;
      this.payuCardErrorMessages = [];
    } else {
      this.selectedCardMethod = null;
      this.tryInitPayUSecureForm();
    }
  }

  private initPaymentProviderWidget(): void {
    const requestModel = new OrderPaymentRequestModel();
    requestModel.channel = this.paymentChannel;

    const orderPaymentPackage = new OrderPaymentPackage();
    orderPaymentPackage.cinemaId = this.order.cinemaId;
    orderPaymentPackage.orderId = this.order.id;
    orderPaymentPackage.paymentProviderIdentifier = this.paymentProviderIdentifier;
    orderPaymentPackage.requestModel = requestModel;

    const fork = forkJoin([
      this.orderDataProvider.getPaymentProviderConfig(orderPaymentPackage),
      this.orderDataProvider.getPaymentProviderPayMethodCollection(orderPaymentPackage)
    ]);

    this.paymentProviderInitSubscription = fork.subscribe({
      next: ([paymentProviderConfigModel, paymentProviderPayMethodCollection]) => {
        this.paymentProviderConfig = paymentProviderConfigModel;
        this.paymentProviderPayMethodCollection = paymentProviderPayMethodCollection;

        this.payByLinkMethodCollection = this.getPayByLinkPayMethodCollection();
        this.cardMethodCollection = this.getCardMethodCollection();
        this.scheduleSecureFormInit();

        this.events.next(new DonePaymentProviderEvent());
      },
      error: (e: HttpErrorResponse) => this.events.next(new ErrorPaymentProviderEvent(e))
    });
  }

  private async tryInitPayUSecureForm(): Promise<void> {
    if (this.isPayuSecureFormInitialized || this.isPayuSecureFormInitInProgress) {
      return;
    }

    if (this.selectedPayMethodTypeCustom !== PaymentMethodEnum.TYPE_CARD || !this.paymentProviderConfig?.salesPointId) {
      return;
    }

    if (!document.querySelector(this.secureFormNumberContainerSelector)) {
      return;
    }

    this.isPayuSecureFormInitInProgress = true;

    try {
      await this.ensurePayUSdkLoaded();
      this.payuExpressService.initializeSecureCardForm(this.paymentProviderConfig.salesPointId, this.secureFormSelectors);
      this.isPayuSecureFormInitialized = true;
    } catch (error) {
      console.error('PayU SecureForms init error', error);
      this.payuCardErrorMessages = ['Unable to initialize PayU SecureForms.'];
    } finally {
      this.isPayuSecureFormInitInProgress = false;
    }
  }

  private ensurePayUSdkLoaded(): Promise<void> {
    if (typeof (window as any).PayU === 'function') {
      return Promise.resolve();
    }

    if (this.payuSdkLoadPromise) {
      return this.payuSdkLoadPromise;
    }

    const scriptSrc = this.getPayUSdkScriptUrl();

    this.payuSdkLoadPromise = new Promise<void>((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${scriptSrc}"]`) as HTMLScriptElement | null;

      const onLoaded = () => {
        if (typeof (window as any).PayU === 'function') {
          resolve();
        } else {
          reject(new Error('PayU SDK script loaded but PayU is unavailable.'));
        }
      };

      const onError = () => reject(new Error(`Unable to load PayU SDK script: ${scriptSrc}`));

      if (existingScript) {
        existingScript.addEventListener('load', onLoaded, { once: true });
        existingScript.addEventListener('error', onError, { once: true });

        if ((existingScript as any).readyState === 'complete') {
          onLoaded();
        }

        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = scriptSrc;
      script.addEventListener('load', onLoaded, { once: true });
      script.addEventListener('error', onError, { once: true });
      document.head.appendChild(script);
    }).finally(() => {
      if (typeof (window as any).PayU !== 'function') {
        this.payuSdkLoadPromise = null;
      }
    });

    return this.payuSdkLoadPromise;
  }

  private getPayUSdkScriptUrl(): string {
    return `${this.environment?.production ? 'https://secure.payu.com' : 'https://secure.snd.payu.com'}/javascript/sdk`;
  }

  private get secureFormNumberContainerSelector(): string {
    return `#${this.secureFormNumberContainerId}`;
  }

  private get secureFormDateContainerSelector(): string {
    return `#${this.secureFormDateContainerId}`;
  }

  private get secureFormCvvContainerSelector(): string {
    return `#${this.secureFormCvvContainerId}`;
  }

  private get secureFormSelectors() {
    return {
      number: this.secureFormNumberContainerSelector,
      date: this.secureFormDateContainerSelector,
      cvv: this.secureFormCvvContainerSelector
    };
  }

  private scheduleSecureFormInit(): void {
    void this.tryInitPayUSecureForm();

    if (this.isPayuSecureFormInitialized) {
      return;
    }

    if (this.secureFormInitTimeoutId !== null) {
      clearTimeout(this.secureFormInitTimeoutId);
    }

    this.secureFormInitTimeoutId = setTimeout(() => {
      this.secureFormInitTimeoutId = null;
      void this.tryInitPayUSecureForm();
    }, 0);
  }

  get getDefaultPaymentImageUrl() {
    return `/assets/images/payment-providers/PAYU_lime_logo.png`;
  }

  private emitNotSelectedPaymentProvider() {
    this.events.emit(new ErrorPaymentKeyProviderEvent(generalPaymentError.NotSelectedPaymentProvider));
  }
}
